<template>
  <div class="w-full flex flex-col gap-4 pt-2">
    <h1 class="text-xl font-bold text-center">Choose Payment Method</h1>

    <div class="grid grid-cols-2 gap-1.5 sm:bg-gray-100 p-1 rounded-2xl sm:flex sm:rounded-full">
      <button
        v-for="tab in availableTabs"
        :key="tab.method"
        :class="[
          'py-2 px-2 sm:px-3 rounded-full text-xs sm:text-sm font-semibold transition-all text-center sm:flex-1 sm:whitespace-nowrap',
          activeTab === tab.method
            ? 'bg-teal-600 text-white shadow-sm'
            : 'text-gray-500 hover:text-gray-700 sm:bg-transparent',
        ]"
        @click="activeTab = tab.method"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="flex items-center justify-center gap-2 py-2">
      <span class="text-4xl font-bold text-teal-600">{{ displayCurrency(props.amountCents) }}</span>
      <button class="text-gray-400 hover:text-gray-600" title="Edit amount" @click="emit('back')">
        <EditPencilIcon class="w-5 h-5" />
      </button>
    </div>
    <div v-if="activeTab === 'CARD'" class="flex flex-col gap-2">
      <p class="text-sm text-gray-500 text-center">
        Select the Credit or Debit card you will use for making the payment.
      </p>
    </div>
    <div v-else-if="activeTab === 'EFT'" class="flex flex-col items-center py-4">
      <p class="text-sm text-gray-500 text-center">
        Please wait for your browser to redirect you back after making the EFT payment.
      </p>
    </div>
    <div v-else-if="activeTab === 'PAY_AT'" class="flex flex-col items-center gap-2 py-4">
      <p class="text-sm text-gray-600 text-center">
        Pay in cash at your nearest Pay@ money counter using your reference number.
      </p>
    </div>
    <div v-else-if="activeTab === 'DEBI_CHECK'" class="flex flex-col items-center gap-2 py-4">
      <p class="text-sm text-gray-600 text-center">Set up a DebiCheck debit order mandate with your bank.</p>
    </div>

    <div v-if="!hasAnyMethod" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
      No payment methods available for this order.
    </div>
    <div class="flex gap-2 pt-2 justify-center">
      <button
        class="w-12 h-12 shrink-0 flex items-center justify-center rounded-xl border-2 border-gray-200 hover:bg-gray-50"
        @click="emit('back')"
      >
        <ArrowLeft class="w-5 h-5" />
      </button>
      <button
        class="flex-1 bg-teal-600 text-white font-bold py-3 rounded-xl hover:bg-teal-700 disabled:opacity-60 max-w-[150px]"
        :disabled="!hasAnyMethod"
        @click="onProceed"
      >
        Make Payment
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import EditPencilIcon from '@/components/icons/EditPencilIcon.vue';
import { displayCurrency } from '@/utils/format.utils';
import { ArrowLeft } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

export type ContributePaymentMethod = 'CARD' | 'EFT' | 'PAY_AT' | 'DEBI_CHECK';

const props = defineProps<{
  order: ModelOrderResponse;
  amountCents: number;
}>();

const emit = defineEmits<{
  select: [method: ContributePaymentMethod];
  back: [];
}>();

type Tab = { method: ContributePaymentMethod; label: string };

const availableTabs = computed<Tab[]>(() => {
  const paymentMethod = props.order.paymentMethods;
  const tabs: Tab[] = [];

  if (paymentMethod?.creditOrDebitCard) {
    tabs.push({ method: 'CARD', label: 'Credit & Debit Card' });
  }
  if (paymentMethod?.eft) {
    tabs.push({ method: 'EFT', label: 'Instant EFT' });
  }
  if (paymentMethod?.payAt) {
    tabs.push({ method: 'PAY_AT', label: 'Cash (in-store)' });
  }
  if (paymentMethod?.debiCheck && props.order.state === 'PLACED') {
    tabs.push({ method: 'DEBI_CHECK', label: 'DebiCheck' });
  }

  return tabs;
});

const activeTab = ref<ContributePaymentMethod>(availableTabs.value[0]?.method ?? 'CARD');

const hasAnyMethod = computed(() => availableTabs.value.length > 0);

const onProceed = () => {
  emit('select', activeTab.value);
};
</script>
